import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate } from "react-router-dom";
import TopMenuBar from "components/TopMenuBar";
import FooterSection from "components/FooterSection";
import LeadCaptureModal from "components/LeadCaptureModal";
import { Heading, Text } from "../../components";
import "./style.css";
import "./lab.css";

const tabs = [
  {
    id: "voice",
    label: "Tone That Adapts",
    product: "Emotion API & SDK",
    proves: "Same words won't do — tone, pace, and reply strategy change with how the person actually feels.",
  },
  {
    id: "agent",
    label: "Reads the Room",
    product: "Relationship Engine",
    proves: "The agent detects a specific emotion and switches strategy — ownership, pacing, escalation — not just tone of voice.",
  },
  {
    id: "article",
    label: "Sounds Human",
    product: "Text Humanizer",
    proves: "Same facts, rewritten with relationship context instead of generic AI phrasing.",
  },
  {
    id: "perception",
    label: "Sees How You Feel",
    product: "AI Perception SDK",
    proves: "Live, on-device facial expression reading. Heartbeat, calmness, and gaze are part of the same API, landing in this demo next.",
  },
];

const tabCtas = {
  voice: {
    primary: { label: "Try the Emotion API", href: "https://developer.aihumanity.io", external: true },
  },
  agent: {
    primary: { label: "Request the Relationship Engine API", action: "modal" },
    secondary: { label: "See the Support De-escalation use case", href: "/use-cases/support-de-escalation", external: false },
  },
  article: {
    primary: { label: "Try the Text Humanizer", href: "/products/text-humanizer", external: false },
  },
  perception: {
    primary: { label: "Test the Perception API", href: "/perception", external: false },
  },
};

const metrics = {
  article: [
    ["Readability", "+37%"],
    ["Brand warmth", "+61%"],
    ["AI stiffness", "-48%"],
  ],
  perception: [
    ["Model latency", "~50ms"],
    ["Expressions", "7"],
    ["On-device", "Private"],
  ],
};

const voiceScenarios = [
  {
    id: "billing",
    label: "Billing frustration",
    context: "Customer's card was declined and service is about to be cut off.",
    without: {
      heading: "Same answer. Same tone. Wrong moment.",
      text: "Your account shows a failed payment. Please update your card to restore service.",
      signals: ["Flat pace", "No stress read", "Script lock"],
    },
    with: {
      heading: "Tone, pace, and repair strategy adapt live.",
      text: "I can hear this is frustrating. I will slow down, explain what happened, and get you back online first.",
      signals: ["Slower tempo", "Frustration: high", "Repair mode"],
    },
    metrics: [
      ["Repair speed", "+41%"],
      ["Felt heard", "+68%"],
      ["Escalation risk", "-32%"],
    ],
  },
  {
    id: "confused",
    label: "Confused new user",
    context: "First-time user can't find a setting and is starting to second-guess themselves.",
    without: {
      heading: "Repeats the same instructions, faster.",
      text: "The setting is located in Preferences under Account. Please navigate there to continue.",
      signals: ["No pacing change", "Assumes fluency", "Same script"],
    },
    with: {
      heading: "Slows down and checks in before moving on.",
      text: "No worries, that one's easy to miss. Let's go there together, tell me what you see on your screen right now.",
      signals: ["Slower tempo", "Confusion: high", "Guided mode"],
    },
    metrics: [
      ["Task completion", "+29%"],
      ["Felt supported", "+54%"],
      ["Repeat contact", "-22%"],
    ],
  },
  {
    id: "calm",
    label: "Casual check-in",
    context: "Low-stakes, relaxed conversation, EQ should not overcorrect into false urgency.",
    without: {
      heading: "Treats every message with the same weight.",
      text: "Your request has been received. Processing time is 24 to 48 hours. Thank you for your patience.",
      signals: ["Flat pace", "No mood read", "One register"],
    },
    with: {
      heading: "Matches the low-key mood instead of manufacturing drama.",
      text: "Got it, no rush on this one. I'll have it sorted in a day or two, and I'll ping you the moment it's done.",
      signals: ["Relaxed tempo", "Calm: high", "Light tone"],
    },
    metrics: [
      ["Perceived warmth", "+45%"],
      ["Over-formality", "-38%"],
      ["Trust lift", "+26%"],
    ],
  },
  {
    id: "vulnerable",
    label: "Vulnerable moment",
    context: "User is overwhelmed and the situation is more personal than transactional.",
    without: {
      heading: "Processes the ask like any other ticket.",
      text: "I understand your account has been suspended. Please submit form 12-B and allow 5 to 7 business days for review.",
      signals: ["Flat pace", "No stress read", "Policy-first"],
    },
    with: {
      heading: "Leads with the person before the process.",
      text: "That sounds like a lot to deal with right now. Let's take this one step at a time, I'm not going anywhere until it's sorted.",
      signals: ["Slower tempo", "Distress: high", "Care-first mode"],
    },
    metrics: [
      ["Felt heard", "+71%"],
      ["Trust lift", "+58%"],
      ["Escalation risk", "-44%"],
    ],
  },
];

const agentScenarios = [
  {
    id: "policy",
    label: "Policy roadblock",
    context: "Company policy is getting in the way of a fast fix.",
    emotions: [
      ["Frustration", 86, "#ff6b6b"],
      ["Confusion", 64, "#ffd166"],
      ["Trust", 38, "#6ddcff"],
      ["Calmness", 31, "#7e60f8"],
    ],
    generic: "I understand. Here are the policy details. Please select an option to continue.",
    emotionAware: "You're trying to fix this fast and the policy is getting in the way. I can take ownership and give you the shortest path.",
    metrics: [
      ["Frustration read", "0.86"],
      ["Strategy shift", "Active"],
      ["Trust lift", "+54%"],
    ],
  },
  {
    id: "repeated",
    label: "Repeated failure",
    context: "This is the user's third attempt at the same task.",
    emotions: [
      ["Frustration", 74, "#ff6b6b"],
      ["Fatigue", 68, "#ffd166"],
      ["Trust", 29, "#6ddcff"],
      ["Patience", 18, "#7e60f8"],
    ],
    generic: "Let's try that again. Please re-enter your information and submit the form.",
    emotionAware: "I see this is the third time, that's on us, not you. Let me just fix it directly instead of asking you to try again.",
    metrics: [
      ["Repeat detection", "3rd try"],
      ["Strategy shift", "Direct fix"],
      ["Frustration drop", "-47%"],
    ],
  },
  {
    id: "newuser",
    label: "First-time confusion",
    context: "Brand-new user unsure what to do next.",
    emotions: [
      ["Confusion", 71, "#ffd166"],
      ["Anxiety", 52, "#ff6b6b"],
      ["Trust", 44, "#6ddcff"],
      ["Curiosity", 61, "#7e60f8"],
    ],
    generic: "Please refer to the documentation for setup instructions.",
    emotionAware: "First time here, totally normal to feel unsure. Want me to walk you through the first step right now?",
    metrics: [
      ["Onboarding drop-off", "-33%"],
      ["Strategy shift", "Guided"],
      ["Trust lift", "+41%"],
    ],
  },
  {
    id: "timepressure",
    label: "Time-pressured",
    context: "User has a deadline and is stressed about time.",
    emotions: [
      ["Urgency", 82, "#ff6b6b"],
      ["Stress", 66, "#ffd166"],
      ["Trust", 35, "#6ddcff"],
      ["Focus", 58, "#7e60f8"],
    ],
    generic: "Your request is in the queue. Estimated response time is 2 business days.",
    emotionAware: "Sounds like you're up against a deadline, let me flag this as urgent and stay on it myself instead of the queue.",
    metrics: [
      ["Urgency detected", "High"],
      ["Strategy shift", "Escalated"],
      ["Time-to-resolution", "-61%"],
    ],
  },
];

function useSpeech() {
  const [speakingId, setSpeakingId] = useState(null);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const speak = (id, text, { rate = 1, pitch = 1 } = {}) => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    if (speakingId === id) {
      setSpeakingId(null);
      return;
    }
    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.onend = () => setSpeakingId((current) => (current === id ? null : current));
    utterance.onerror = () => setSpeakingId((current) => (current === id ? null : current));
    setSpeakingId(id);
    window.speechSynthesis.speak(utterance);
  };

  return { speakingId, speak };
}

const extraDemos = [
  {
    title: "Game NPC Relationship",
    product: "VoicePlay Engine",
    before: "Repeats quest lines after the player fails twice.",
    after: "Detects stress, lowers verbal pressure, remembers the failed path, and offers a calmer route.",
    href: "/use-cases/player-retention",
    external: false,
  },
  {
    title: "Companion Continuity",
    product: "AI Companion",
    before: "Starts each morning like a fresh chatbot session.",
    after: "Carries yesterday's mood, preference drift, and trust trajectory into today's conversation.",
    href: "/use-cases/companionship",
    external: false,
  },
  {
    title: "Robot Social Timing",
    product: "Humanoid Robotics",
    before: "Speaks while the user looks away and misses hesitation.",
    after: "Uses gaze, breath, and pause length to wait, soften, or ask a better next question.",
    href: "/#robotics",
    external: false,
  },
];

function DemoBadge({ children }) {
  return <span className="demo-badge">{children}</span>;
}

function MetricStrip({ metrics: rows, label }) {
  return (
    <div className="demo-metrics-wrap">
      <div className="demo-metrics" aria-label={`${label} demo metrics`}>
        {rows.map(([metricLabel, value]) => (
          <div className="demo-metric" key={metricLabel}>
            <strong>{value}</strong>
            <span>{metricLabel}</span>
          </div>
        ))}
      </div>
      <p className="demo-metrics-note">Illustrative demo outcomes, not measured results.</p>
    </div>
  );
}

function ScenarioPicker({ scenarios, activeId, onSelect, label }) {
  return (
    <div className="scenario-picker" role="tablist" aria-label={label}>
      {scenarios.map((scenario) => (
        <button
          key={scenario.id}
          className={activeId === scenario.id ? "active" : ""}
          onClick={() => onSelect(scenario.id)}
          type="button"
          role="tab"
          aria-selected={activeId === scenario.id}
        >
          {scenario.label}
        </button>
      ))}
    </div>
  );
}

function Waveform({ adaptive }) {
  const bars = [36, 58, 30, 72, 44, 66, 28, 78, 52, 38, 68, 46, 76, 34, 62, 42];
  return (
    <div className={`waveform ${adaptive ? "is-adaptive" : ""}`} aria-hidden="true">
      {bars.map((height, index) => (
        <span
          key={`${height}-${index}`}
          style={{
            height: adaptive ? `${Math.min(88, height + (index % 3) * 8)}%` : `${height}%`,
            animationDelay: `${index * 0.045}s`,
          }}
        />
      ))}
    </div>
  );
}

function PlayButton({ isSpeaking, onClick, disabled }) {
  return (
    <button type="button" className="play-button" onClick={onClick} disabled={disabled}>
      {isSpeaking ? "■ Stop" : "▶ Hear it"}
    </button>
  );
}

function VoiceDemo() {
  const [activeId, setActiveId] = useState(voiceScenarios[0].id);
  const active = useMemo(
    () => voiceScenarios.find((scenario) => scenario.id === activeId) || voiceScenarios[0],
    [activeId],
  );
  const { speakingId, speak } = useSpeech();
  const canSpeak = typeof window !== "undefined" && "speechSynthesis" in window;

  return (
    <div className="voice-demo">
      <ScenarioPicker
        scenarios={voiceScenarios}
        activeId={activeId}
        onSelect={setActiveId}
        label="Voice EQ scenarios"
      />
      <p className="scenario-context">{active.context}</p>
      {canSpeak && (
        <p className="demo-tts-note">
          "Hear it" uses your browser's speech synthesis to illustrate tone and pacing — not the product's real voice output.
        </p>
      )}

      <div className="demo-grid two-col">
        <div className="compare-panel muted">
          <div className="panel-kicker">Without EQ</div>
          <Waveform />
          <Heading size="md" as="h3">{active.without.heading}</Heading>
          <Text size="sm">"{active.without.text}"</Text>
          <div className="signal-row">
            {active.without.signals.map((signal) => (
              <span key={signal}>{signal}</span>
            ))}
          </div>
          {canSpeak && (
            <PlayButton
              isSpeaking={speakingId === `${active.id}-without`}
              onClick={() => speak(`${active.id}-without`, active.without.text, { rate: 1.05, pitch: 1 })}
            />
          )}
        </div>

        <div className="compare-panel hot">
          <div className="panel-kicker">With AIHumanity EQ</div>
          <Waveform adaptive />
          <Heading size="md" as="h3">{active.with.heading}</Heading>
          <Text size="sm">"{active.with.text}"</Text>
          <div className="signal-row">
            {active.with.signals.map((signal) => (
              <span key={signal}>{signal}</span>
            ))}
          </div>
          {canSpeak && (
            <PlayButton
              isSpeaking={speakingId === `${active.id}-with`}
              onClick={() => speak(`${active.id}-with`, active.with.text, { rate: 0.88, pitch: 0.94 })}
            />
          )}
        </div>
      </div>

      <MetricStrip metrics={active.metrics} label="voice" />
    </div>
  );
}

function AgentDemo() {
  const [activeId, setActiveId] = useState(agentScenarios[0].id);
  const active = useMemo(
    () => agentScenarios.find((scenario) => scenario.id === activeId) || agentScenarios[0],
    [activeId],
  );

  return (
    <div className="agent-demo">
      <ScenarioPicker
        scenarios={agentScenarios}
        activeId={activeId}
        onSelect={setActiveId}
        label="Agent EQ scenarios"
      />
      <p className="scenario-context">{active.context}</p>

      <div className="agent-stage">
        <div className="emotion-stack">
          {active.emotions.map(([label, value, color]) => (
            <div className="emotion-meter" key={label}>
              <div>
                <span>{label}</span>
                <strong>{value}%</strong>
              </div>
              <i style={{ "--fill": `${value}%`, "--color": color }} />
            </div>
          ))}
        </div>

        <div className="dialogue-compare">
          <div className="dialogue-card">
            <span>Generic agent</span>
            <p>"{active.generic}"</p>
          </div>
          <div className="dialogue-card selected">
            <span>Emotion-aware agent</span>
            <p>"{active.emotionAware}"</p>
          </div>
        </div>
      </div>

      <MetricStrip metrics={active.metrics} label="agent" />
    </div>
  );
}

function ArticleDemo() {
  return (
    <div className="article-compare">
      <article>
        <span>AI generated</span>
        <h3>AI systems enhance interaction through adaptive personalization.</h3>
        <p>
          The platform utilizes emotional context to optimize user engagement and increase
          retention across diverse verticals. This results in improved experiences and better
          outcomes for stakeholders.
        </p>
      </article>
      <article className="human">
        <span>Humanized by relationship context</span>
        <h3>The product changes when it can tell how the person is doing.</h3>
        <p>
          A stressed player does not need the same line again. A tired customer does not need
          another menu. AIHumanity gives the product enough emotional context to choose the
          next move with care.
        </p>
      </article>
    </div>
  );
}

const PERCEPTION_MODES = [
  { id: "facial", label: "Facial expression", sub: "Live camera demo" },
  { id: "biosignal", label: "Heartbeat \u00b7 calmness \u00b7 gaze", sub: "Coming soon" },
];

function FacialExpressionDemo() {
  const frameRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === frameRef.current);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = async () => {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
      return;
    }
    await frameRef.current?.requestFullscreen();
  };

  return (
    <div className="fer-demo-frame" ref={frameRef}>
      <button
        className="fer-fullscreen-button"
        type="button"
        onClick={toggleFullscreen}
        aria-label={isFullscreen ? "Close fullscreen" : "Open fullscreen"}
        title={isFullscreen ? "Close fullscreen" : "Open fullscreen"}
      >
        {isFullscreen ? "\u00d7" : "\u26f6"}
      </button>
      <iframe
        src="/fer-test.html?embed=1"
        title="Live facial expression recognition demo"
        allow="camera"
        allowFullScreen
      />
    </div>
  );
}

function BiosignalComingSoon() {
  return (
    <div className="perception-coming-soon">
      <span className="demo-badge">Coming soon</span>
      <Heading size="md" as="h3">Heartbeat, calmness, and gaze aren't wired into this demo yet</Heading>
      <p>
        These signals are part of the AI Perception API today \u2014 remote heart rate, calmness,
        and eye-gaze detection from a standard camera \u2014 but this page only demos the live
        facial-expression model so far.
      </p>
      <Link className="demo-tab-cta-button primary" to="/perception">
        See how it works on the Perception API page
      </Link>
    </div>
  );
}

function PerceptionDemo() {
  const [subMode, setSubMode] = useState("facial");

  return (
    <div className="perception-demo">
      <div className="scenario-picker" role="tablist" aria-label="Perception signal">
        {PERCEPTION_MODES.map((option) => (
          <button
            key={option.id}
            className={subMode === option.id ? "active" : ""}
            onClick={() => setSubMode(option.id)}
            type="button"
            role="tab"
            aria-selected={subMode === option.id}
          >
            {option.label}
          </button>
        ))}
      </div>

      {subMode === "facial" ? (
        <>
          <FacialExpressionDemo />
          <MetricStrip metrics={metrics.perception} label="perception" />
        </>
      ) : (
        <BiosignalComingSoon />
      )}
    </div>
  );
}

function ActiveDemo({ active }) {
  if (active === "voice") return <VoiceDemo />;
  if (active === "agent") return <AgentDemo />;
  if (active === "article") return <ArticleDemo />;
  return <PerceptionDemo />;
}

function renderCtaLink(cta, onOpenModal) {
  if (!cta) return null;
  if (cta.action === "modal") {
    return (
      <button type="button" className="demo-tab-cta-button primary" onClick={() => onOpenModal(cta)}>
        {cta.label}
      </button>
    );
  }
  return cta.external ? (
    <a
      className="demo-tab-cta-button primary"
      href={cta.href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {cta.label}
    </a>
  ) : (
    <Link className="demo-tab-cta-button primary" to={cta.href}>
      {cta.label}
    </Link>
  );
}

function TabCta({ tab, onOpenModal }) {
  const cta = tabCtas[tab.id];
  if (!cta) return null;
  return (
    <div className="demo-tab-cta">
      <div className="demo-tab-cta-actions">
        {renderCtaLink(cta.primary, onOpenModal)}
        {cta.secondary && (
          <Link className="demo-tab-cta-button secondary" to={cta.secondary.href}>
            {cta.secondary.label}
          </Link>
        )}
      </div>
    </div>
  );
}

function DemoLab() {
  const location = useLocation();
  const navigate = useNavigate();
  const requestedTab = new URLSearchParams(location.search).get("tab");
  const [active, setActive] = useState(() => (
    tabs.some((tab) => tab.id === requestedTab) ? requestedTab : "voice"
  ));
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContext, setModalContext] = useState("");
  const activeTab = useMemo(() => tabs.find((tab) => tab.id === active), [active]);

  useEffect(() => {
    if (tabs.some((tab) => tab.id === requestedTab) && requestedTab !== active) {
      setActive(requestedTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedTab]);

  useEffect(() => {
    if (location.hash !== "#demo-lab") return;
    const el = document.getElementById("demo-lab");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  function selectTab(id) {
    setActive(id);
    const params = new URLSearchParams(location.search);
    params.set("tab", id);
    navigate({ pathname: "/demo", search: `?${params.toString()}` }, { replace: true });
  }

  function openModal() {
    setModalContext(`Interested in: ${activeTab.product}`);
    setModalOpen(true);
  }

  return (
    <section className="demo-lab" id="demo-lab">
      <div className="demo-section-head">
        <DemoBadge>Interactive Product Demo</DemoBadge>
        <Heading size="2xl" as="h2">Before AIHumanity, AI responds. After AIHumanity, it relates.</Heading>
      </div>

      <div className="demo-tabs" role="tablist" aria-label="AIHumanity demo products">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={active === tab.id ? "active" : ""}
            onClick={() => selectTab(tab.id)}
            type="button"
            role="tab"
            aria-selected={active === tab.id}
          >
            <span>{tab.label}</span>
            <small>{tab.product}</small>
          </button>
        ))}
      </div>

      <div className="demo-product-shell">
        <div className="demo-product-title">
          <span>{activeTab.product}</span>
          <strong>{activeTab.label}</strong>
        </div>
        <p className="demo-tab-proves"><strong>What this proves:</strong> {activeTab.proves}</p>
        <ActiveDemo active={active} />
        {active === "article" && (
          <MetricStrip metrics={metrics[active]} label={active} />
        )}
        <TabCta tab={activeTab} onOpenModal={openModal} />
      </div>

      <LeadCaptureModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        kind="contact"
        context={modalContext}
      />
    </section>
  );
}

function DemoHero() {
  return (
    <section className="demo-hero">
      <div className="demo-hero-copy">
        <DemoBadge>AIHumanity Product Line</DemoBadge>
        <Heading size="4xl" as="h1">
          Demo the jump from intelligent AI to emotionally aware AI.
        </Heading>
        <Text size="lg">
          Voice, agents, content, perception, games, companions, and robots. One relationship
          layer that detects human state, updates memory, and adapts behavior in real time.
        </Text>
        <div className="hero-actions">
          <a className="hero-action-primary" href="#demo-lab">Open Demo Lab</a>
          <Link className="hero-action-secondary" to="/demo/voice-agent">Live Voice Agent</Link>
          <a
            className="hero-action-tertiary"
            href="https://developer.aihumanity.io"
            target="_blank"
            rel="noopener noreferrer"
          >
            SDK Access →
          </a>
        </div>
      </div>

      <div className="hero-console" aria-label="AIHumanity relationship layer preview">
        <video src="/video/AIHumanityDemo.mov" autoPlay muted loop playsInline />
        <div className="console-overlay">
          <span>Detect</span>
          <span>Update</span>
          <span>Adapt</span>
        </div>
        <div className="relationship-core">
          <i />
          <strong>Relationship State</strong>
          <span>mood + memory + behavior</span>
        </div>
      </div>
    </section>
  );
}

function ProductLine() {
  return (
    <section className="product-line">
      <div className="demo-section-head">
        <DemoBadge>More Demos</DemoBadge>
        <Heading size="2xl" as="h2">Every demo uses the same loop: detect, update, adapt.</Heading>
      </div>
      <div className="product-cards">
        {extraDemos.map((demo) => (
          <Link className="product-card" key={demo.title} to={demo.href}>
            <span>{demo.product}</span>
            <Heading size="md" as="h3">{demo.title}</Heading>
            <div className="before-after">
              <p><strong>Before</strong>{demo.before}</p>
              <p><strong>After</strong>{demo.after}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function ArchitectureStrip() {
  return (
    <section className="architecture-strip">
      {["Multimodal Understanding", "Persistent State", "Behavior Adaptation", "Any Model"].map((item, index) => (
        <div key={item}>
          <span>0{index + 1}</span>
          <strong>{item}</strong>
        </div>
      ))}
    </section>
  );
}

export default function DemoPage() {
  return (
    <>
      <Helmet>
        <title>AIHumanity Product Demo — Emotion-Aware AI</title>
        <meta
          name="description"
          content="Explore AIHumanity demos across voice EQ, emotion-aware agents, humanized content, AI Perception, games, companions, and robotics."
        />
      </Helmet>

      <div className="demo-page">
        <TopMenuBar />
        <main>
          <DemoHero />
          <ArchitectureStrip />
          <DemoLab />
          <ProductLine />
        </main>
        <FooterSection />
      </div>
    </>
  );
}
